import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Building2, 
  Check,
  ArrowLeft,
  ArrowRight,
  User,
  Mail,
  Phone
} from 'lucide-react';
import { format, addDays, isBefore, startOfToday, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "/brand/zoea-origin-mark.svg";

const TIME_SLOTS = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
  '20:00', '20:30', '21:00', '21:30', '22:00'
];

const PublicBookingPage = () => {
  const { orgId } = useParams();
  const [step, setStep] = useState(1);
  const [resources, setResources] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingResult, setBookingResult] = useState({ status: 'pending', email_sent: false });
  const [branding, setBranding] = useState({
    name: 'Zoea',
    logo_url: '',
  });
  
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    description: ''
  });

  const fetchResources = useCallback(async () => {
    try {
      const [orgResponse, resourcesResponse] = await Promise.all([
        axios.get(`${API}/public/organization/${orgId}`),
        axios.get(`${API}/public/resources/${orgId}`),
      ]);
      setBranding({
        name: orgResponse.data?.name || 'Zoea',
        logo_url: orgResponse.data?.logo_url || '',
      });
      setResources(resourcesResponse.data);
    } catch (error) {
      toast.error('Kon resources niet laden');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const fetchAvailability = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API}/public/availability/${orgId}/${selectedResource.id}?date=${dateStr}`
      );
      setBookedSlots(response.data.booked_slots || []);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  }, [orgId, selectedDate, selectedResource]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  useEffect(() => {
    if (selectedResource && selectedDate) {
      fetchAvailability();
    }
  }, [fetchAvailability, selectedResource, selectedDate]);

  const isTimeSlotBooked = (time) => {
    if (!selectedDate) return false;
    
    const slotDateTime = new Date(selectedDate);
    const [hours, minutes] = time.split(':');
    slotDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    return bookedSlots.some(slot => {
      const startTime = typeof slot.start_time === 'string' 
        ? parseISO(slot.start_time) 
        : new Date(slot.start_time);
      const endTime = typeof slot.end_time === 'string' 
        ? parseISO(slot.end_time) 
        : new Date(slot.end_time);
      
      return slotDateTime >= startTime && slotDateTime < endTime;
    });
  };

  const getNextAvailableEndTime = (startTime) => {
    const startIndex = TIME_SLOTS.findIndex((slot) => slot === startTime);
    if (startIndex < 0) return '';
    for (let i = startIndex + 1; i < TIME_SLOTS.length; i += 1) {
      if (!isTimeSlotBooked(TIME_SLOTS[i])) return TIME_SLOTS[i];
    }
    return '';
  };

  const handleQuickSlotPick = (time) => {
    if (isTimeSlotBooked(time)) return;
    setSelectedStartTime(time);
    const endTime = getNextAvailableEndTime(time);
    setSelectedEndTime(endTime);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email) {
      toast.error('Vul je naam en e-mail in');
      return;
    }

    setSubmitting(true);
    
    try {
      const startDateTime = new Date(selectedDate);
      const [startHours, startMinutes] = selectedStartTime.split(':');
      startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
      
      const endDateTime = new Date(selectedDate);
      const [endHours, endMinutes] = selectedEndTime.split(':');
      endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
      
      const response = await axios.post(`${API}/public/book/${orgId}`, {
        title: formData.title || `Boeking ${formData.customer_name}`,
        resource_id: selectedResource.id,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        description: formData.description,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone
      });
      setBookingResult({
        status: response.data?.status || 'pending',
        email_sent: !!response.data?.email_sent,
      });
      setBookingComplete(true);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Dit tijdslot is helaas al geboekt');
      } else {
        toast.error('Boeking mislukt. Probeer het opnieuw.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && !selectedResource) {
      toast.error('Selecteer eerst een resource');
      return;
    }
    if (step === 2 && (!selectedDate || !selectedStartTime || !selectedEndTime)) {
      toast.error('Selecteer een datum en tijd');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);
  const bookingSourceLabel =
    branding.name.trim().toLowerCase() === 'zoea'
      ? branding.name
      : `${branding.name} via Zoea`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-200"></div>
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Manrope']">
              Boeking ontvangen!
            </h2>
            <p className="text-slate-600 mb-6">
              {bookingResult.status === 'confirmed'
                ? 'Je aanvraag is automatisch geaccepteerd.'
                : 'Je aanvraag is ontvangen en wacht op goedkeuring.'}
              {bookingResult.email_sent ? ' We hebben je ook een e-mail gestuurd.' : ''}
            </p>
            <div className="p-4 bg-slate-50 rounded-xl text-left space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Resource:</span>
                <span className="font-medium">{selectedResource?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Datum:</span>
                <span className="font-medium">{selectedDate && format(selectedDate, 'd MMMM yyyy', { locale: nl })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tijd:</span>
                <span className="font-medium">{selectedStartTime} - {selectedEndTime}</span>
              </div>
            </div>
            <Button 
              onClick={() => window.location.reload()}
              className="mt-6 bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
            >
              Nieuwe boeking maken
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30" data-testid="public-booking-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <img
            src={branding.logo_url || LOGO_URL}
            alt={branding.name}
            className="w-11 h-11 object-contain shrink-0"
          />
          <div>
            <h1 className="text-lg font-bold text-slate-900 font-['Manrope']">Online Reserveren</h1>
            <p className="text-sm text-slate-500">{bookingSourceLabel}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                  step >= s 
                    ? 'bg-teal-600 text-white' 
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 rounded ${step > s ? 'bg-teal-600' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Resource */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">Kies een ruimte of resource</h2>
              <p className="text-slate-500 mt-1">Selecteer wat je wilt reserveren</p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {resources.map((resource) => (
                <Card 
                  key={resource.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedResource?.id === resource.id 
                      ? 'ring-2 ring-teal-500 shadow-lg' 
                      : 'border-slate-200'
                  }`}
                  onClick={() => setSelectedResource(resource)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${resource.color}20` }}
                      >
                        <Building2 className="w-6 h-6" style={{ color: resource.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{resource.name}</h3>
                        {resource.description && (
                          <p className="text-sm text-slate-500 mt-1">{resource.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          {resource.capacity && <span>{resource.capacity} personen</span>}
                          {resource.hourly_rate && <span>€{resource.hourly_rate}/uur</span>}
                        </div>
                      </div>
                      {selectedResource?.id === resource.id && (
                        <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {resources.length === 0 && (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Geen resources beschikbaar</p>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end">
              <Button 
                onClick={nextStep}
                disabled={!selectedResource}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl px-8"
              >
                Volgende
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">Kies datum en tijd</h2>
              <p className="text-slate-500 mt-1">Selecteer wanneer je wilt reserveren</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Calendar */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CalendarIcon className="w-5 h-5 text-teal-600" />
                    Datum
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => isBefore(date, startOfToday())}
                    locale={nl}
                    className="rounded-xl border"
                  />
                </CardContent>
              </Card>

              {/* Time Slots */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Clock className="w-5 h-5 text-teal-600" />
                    Tijd
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedDate ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Starttijd</Label>
                        <Select value={selectedStartTime} onValueChange={setSelectedStartTime}>
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Selecteer starttijd" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_SLOTS.slice(0, -1).map((time) => (
                              <SelectItem 
                                key={time} 
                                value={time}
                                disabled={isTimeSlotBooked(time)}
                              >
                                {time} {isTimeSlotBooked(time) && '(bezet)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Eindtijd</Label>
                        <Select value={selectedEndTime} onValueChange={setSelectedEndTime}>
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Selecteer eindtijd" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_SLOTS.filter(t => t > selectedStartTime).map((time) => (
                              <SelectItem 
                                key={time} 
                                value={time}
                                disabled={isTimeSlotBooked(time)}
                              >
                                {time} {isTimeSlotBooked(time) && '(bezet)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 pt-2">
                        <Label>Beschikbaarheidsoverzicht (klik om te kiezen)</Label>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
                          {TIME_SLOTS.slice(0, -1).map((time) => {
                            const isBooked = isTimeSlotBooked(time);
                            const isSelected = selectedStartTime === time;
                            return (
                              <button
                                key={time}
                                type="button"
                                onClick={() => handleQuickSlotPick(time)}
                                disabled={isBooked}
                                className={`text-xs rounded-lg px-2 py-2 border transition-colors ${
                                  isBooked
                                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                    : isSelected
                                      ? 'bg-teal-600 text-white border-teal-600'
                                      : 'bg-white text-slate-700 border-slate-200 hover:border-teal-400 hover:text-teal-700'
                                }`}
                              >
                                {time}
                                <span className="block text-[10px] opacity-80">
                                  {isBooked ? 'bezet' : 'vrij'}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      Selecteer eerst een datum
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={prevStep} className="rounded-xl">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Terug
              </Button>
              <Button 
                onClick={nextStep}
                disabled={!selectedDate || !selectedStartTime || !selectedEndTime}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl px-8"
              >
                Volgende
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Contact Details */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">Je gegevens</h2>
              <p className="text-slate-500 mt-1">Vul je contactgegevens in om te bevestigen</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Summary */}
              <Card className="border-0 shadow-sm bg-teal-50/50">
                <CardHeader>
                  <CardTitle className="text-lg">Samenvatting</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                    <Building2 className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="text-sm text-slate-500">Resource</p>
                      <p className="font-medium">{selectedResource?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                    <CalendarIcon className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="text-sm text-slate-500">Datum</p>
                      <p className="font-medium">{selectedDate && format(selectedDate, 'EEEE d MMMM yyyy', { locale: nl })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                    <Clock className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="text-sm text-slate-500">Tijd</p>
                      <p className="font-medium">{selectedStartTime} - {selectedEndTime}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Form */}
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="customer_name">Naam *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="customer_name"
                          value={formData.customer_name}
                          onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                          placeholder="Je volledige naam"
                          className="pl-10 rounded-xl"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="customer_email">E-mail *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="customer_email"
                          type="email"
                          value={formData.customer_email}
                          onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                          placeholder="naam@voorbeeld.nl"
                          className="pl-10 rounded-xl"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="customer_phone">Telefoon</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="customer_phone"
                          type="tel"
                          value={formData.customer_phone}
                          onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                          placeholder="+31 6 12345678"
                          className="pl-10 rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title">Doel van de reservering</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Bijv. Teamvergadering"
                        className="rounded-xl"
                      />
                    </div>

                    <div className="pt-4 flex gap-3">
                      <Button 
                        type="button"
                        variant="outline" 
                        onClick={prevStep} 
                        className="flex-1 rounded-xl"
                      >
                        Terug
                      </Button>
                      <Button 
                        type="submit"
                        disabled={submitting}
                        className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                      >
                        {submitting ? 'Bezig...' : 'Reserveren'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicBookingPage;
